import { memo, useEffect, useState } from 'react'
import {
	Box,
	Button,
	Dialog,
	DialogContent,
	Fade,
	FormControlLabel,
	Switch,
	Typography,
} from '@mui/material'
import { WELLBEING_MESSAGES, WELLBEING_THEME } from '../constants/wellbeingMessages'
import { getFirstName } from '../../dashboardGreeting/utils/greetingEngine'
import { useBreathingTimer } from '../hooks/useBreathingTimer'
import BreathingCircle from './BreathingCircle'

const STEPS = {
	INTRO: 'intro',
	BREATHING: 'breathing',
	SUCCESS: 'success',
	SKIPPED: 'skipped',
}

function WellbeingModal({
	open,
	scenario,
	userName,
	onDismiss,
	onRemindLater,
	onContinuePlatform,
	onComplete,
}) {
	const [step, setStep] = useState(STEPS.INTRO)
	const [ambient, setAmbient] = useState(false)
	const reduceMotion =
		typeof window !== 'undefined' &&
		window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches

	const { phase, secondsLeft, scale, running, start, stop, reset } =
		useBreathingTimer({
			active: open && step === STEPS.BREATHING,
			reduceMotion,
		})

	const firstName = getFirstName(userName)
	const copy = WELLBEING_MESSAGES[scenario] || WELLBEING_MESSAGES.manual

	useEffect(() => {
		if (!open) {
			setStep(STEPS.INTRO)
			reset()
			return
		}
		setStep(STEPS.INTRO)
	}, [open, reset])

	useEffect(() => {
		if (!open || step !== STEPS.INTRO) return undefined
		const onKey = (e) => {
			if (e.code === 'Space') {
				e.preventDefault()
				setStep(STEPS.BREATHING)
				start()
			}
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [open, step, start])

	const handleClose = () => {
		stop()
		onDismiss?.()
	}

	const finishBreathing = () => {
		stop()
		setStep(STEPS.SUCCESS)
	}

	const skipToPlatform = () => {
		stop()
		setStep(STEPS.SKIPPED)
	}

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			TransitionComponent={Fade}
			transitionDuration={280}
			aria-labelledby='wellbeing-title'
			aria-describedby='wellbeing-body'
			closeAfterTransition
			BackdropProps={{
				sx: {
					bgcolor: WELLBEING_THEME.backdrop,
					backdropFilter: 'blur(8px)',
				},
			}}
			PaperProps={{
				id: 'wellbeing-modal',
				sx: {
					borderRadius: '24px',
					maxWidth: 440,
					width: '100%',
					m: 2,
					overflow: 'hidden',
					bgcolor: WELLBEING_THEME.glass,
					backdropFilter: 'blur(18px)',
					border: '1px solid rgba(255,255,255,0.65)',
					boxShadow: '0 28px 60px rgba(30, 27, 75, 0.18)',
					'@keyframes wbScaleIn': {
						from: { opacity: 0, transform: 'scale(0.96) translateY(8px)' },
						to: { opacity: 1, transform: 'scale(1) translateY(0)' },
					},
					'@media (prefers-reduced-motion: no-preference)': {
						animation: 'wbScaleIn 0.35s ease',
					},
				},
			}}
		>
			<DialogContent sx={{ px: { xs: 3, sm: 4 }, py: 3.5, textAlign: 'center' }}>
				<Box
					aria-hidden
					sx={{
						width: 56,
						height: 56,
						mx: 'auto',
						mb: 2,
						borderRadius: '50%',
						bgcolor: WELLBEING_THEME.primarySoft,
						display: 'grid',
						placeItems: 'center',
						fontSize: 26,
						boxShadow: '0 8px 20px rgba(91, 75, 219, 0.12)',
					}}
				>
					🌿
				</Box>

				{step === STEPS.INTRO || step === STEPS.BREATHING ? (
					<>
						<Typography
							id='wellbeing-title'
							sx={{
								fontFamily: '"Georgia", "Times New Roman", serif',
								fontSize: { xs: '1.35rem', sm: '1.5rem' },
								fontWeight: 600,
								color: WELLBEING_THEME.text,
								mb: 1.5,
							}}
						>
							Time for you, {firstName}
						</Typography>

						<Box id='wellbeing-body' sx={{ mb: 2 }}>
							{(copy.body || []).map((line) => (
								<Typography
									key={line}
									sx={{
										fontFamily: '"Georgia", "Times New Roman", serif',
										fontSize: '0.95rem',
										color: WELLBEING_THEME.muted,
										lineHeight: 1.55,
									}}
								>
									{line}
								</Typography>
							))}
						</Box>

						<Box
							aria-hidden
							sx={{
								display: 'flex',
								justifyContent: 'center',
								gap: 0.75,
								mb: 1.5,
							}}
						>
							{[0, 1, 2].map((i) => (
								<Box
									key={i}
									sx={{
										width: 18,
										height: 4,
										borderRadius: 99,
										bgcolor:
											i < (step === STEPS.BREATHING ? 3 : 2)
												? WELLBEING_THEME.primary
												: '#DDD6FE',
									}}
								/>
							))}
						</Box>

						{(step === STEPS.BREATHING || step === STEPS.INTRO) && (
							<BreathingCircle
								label={phase.label}
								prompt={
									step === STEPS.BREATHING
										? phase.prompt
										: 'Breathe in slowly…'
								}
								secondsLeft={step === STEPS.BREATHING ? secondsLeft : 4}
								scale={step === STEPS.BREATHING ? scale : 1}
								ambient={ambient && running}
								reduceMotion={reduceMotion}
							/>
						)}

						{/* <FormControlLabel
							sx={{ mt: 0.5, mb: 1.5, color: WELLBEING_THEME.muted }}
							control={
								<Switch
									size='small'
									checked={ambient}
									onChange={(e) => setAmbient(e.target.checked)}
									inputProps={{ 'aria-label': 'Enable relaxing ambient animation' }}
								/>
							}
							label={
								<Typography sx={{ fontSize: '0.75rem' }}>
									Enable relaxing ambient animation (muted)
								</Typography>
							}
						/> */}

						{step === STEPS.INTRO ? (
							<Box
								sx={{
									display: 'flex',
									flexDirection: { xs: 'column', sm: 'row' },
									gap: 1.25,
									mt: 1,
								}}
							>
								<Button
									variant='contained'
									onClick={() => {
										setStep(STEPS.BREATHING)
										start()
									}}
									sx={{
										flex: 1,
										textTransform: 'none',
										fontWeight: 700,
										borderRadius: '12px',
										py: 1.15,
										bgcolor: WELLBEING_THEME.primary,
										boxShadow: 'none',
										'&:hover': { bgcolor: '#4C3DC7', boxShadow: 'none' },
									}}
								>
									Begin breathing
								</Button>
								<Button
									variant='outlined'
									onClick={skipToPlatform}
									sx={{
										flex: 1,
										textTransform: 'none',
										fontWeight: 700,
										borderRadius: '12px',
										py: 1.15,
										color: WELLBEING_THEME.text,
										borderColor: '#CBD5E1',
										bgcolor: '#fff',
									}}
								>
									Not now
								</Button>
							</Box>
						) : (
							<Box sx={{ display: 'flex', gap: 1.25, mt: 1 }}>
								<Button
									variant='contained'
									onClick={finishBreathing}
									sx={{
										flex: 1,
										textTransform: 'none',
										fontWeight: 700,
										borderRadius: '12px',
										bgcolor: WELLBEING_THEME.primary,
									}}
								>
									I&apos;m ready
								</Button>
								<Button
									variant='outlined'
									onClick={skipToPlatform}
									sx={{
										flex: 1,
										textTransform: 'none',
										fontWeight: 700,
										borderRadius: '12px',
										borderColor: '#CBD5E1',
										color: WELLBEING_THEME.text,
									}}
								>
									Continue to Platform
								</Button>
							</Box>
						)}

						<Button
							onClick={onRemindLater}
							sx={{
								mt: 1.5,
								textTransform: 'none',
								color: WELLBEING_THEME.muted,
								fontSize: '0.8125rem',
								textDecoration: 'underline',
								textUnderlineOffset: 3,
							}}
						>
							Remind me later
						</Button>
					</>
				) : null}

				{step === STEPS.SUCCESS ? (
					<>
						<Typography
							id='wellbeing-title'
							sx={{
								fontFamily: '"Georgia", "Times New Roman", serif',
								fontSize: '1.35rem',
								fontWeight: 600,
								color: WELLBEING_THEME.text,
								mb: 1,
							}}
						>
							Your wellbeing check-in
						</Typography>
						<Typography sx={{ color: WELLBEING_THEME.muted, mb: 0.75 }}>
							Well done.
						</Typography>
						<Typography sx={{ color: WELLBEING_THEME.muted, mb: 0.75 }}>
							That minute was for you.
						</Typography>
						<Typography sx={{ color: WELLBEING_THEME.muted, mb: 0.75 }}>
							The students you meet next will benefit from your calm presence.
						</Typography>
						<Typography
							sx={{ fontWeight: 700, color: WELLBEING_THEME.text, mb: 2.5 }}
						>
							You&apos;re ready.
						</Typography>
						<Box sx={{ display: 'flex', gap: 1.25 }}>
							<Button
								variant='contained'
								onClick={onComplete}
								sx={{
									flex: 1,
									textTransform: 'none',
									fontWeight: 700,
									borderRadius: '12px',
									bgcolor: WELLBEING_THEME.primary,
								}}
							>
								Continue Working
							</Button>
							<Button
								variant='outlined'
								onClick={onComplete}
								sx={{
									flex: 1,
									textTransform: 'none',
									fontWeight: 700,
									borderRadius: '12px',
									borderColor: '#CBD5E1',
									color: WELLBEING_THEME.text,
								}}
							>
								Done
							</Button>
						</Box>
					</>
				) : null}

				{step === STEPS.SKIPPED ? (
					<>
						<Typography
							id='wellbeing-title'
							sx={{
								fontFamily: '"Georgia", "Times New Roman", serif',
								fontSize: '1.35rem',
								fontWeight: 600,
								color: WELLBEING_THEME.text,
								mb: 1,
							}}
						>
							Your wellbeing check-in
						</Typography>
						<Typography sx={{ color: WELLBEING_THEME.muted, mb: 0.75 }}>
							That&apos;s okay.
						</Typography>
						<Typography sx={{ color: WELLBEING_THEME.muted, mb: 2.5 }}>
							Whenever you&apos;re ready, your breathing space is waiting in Help
							Center → Wellbeing.
						</Typography>
						<Button
							variant='contained'
							onClick={onContinuePlatform}
							sx={{
								textTransform: 'none',
								fontWeight: 700,
								borderRadius: '12px',
								px: 3,
								bgcolor: WELLBEING_THEME.primary,
							}}
						>
							Continue to Platform
						</Button>
					</>
				) : null}
			</DialogContent>
		</Dialog>
	)
}

export default memo(WellbeingModal)
